import { supabase } from '../config/supabase';
import { setSentryUser, clearSentryUser } from '../config/sentry';
import { User } from '../types';
import { analyticsService } from './analytics';
import { Platform } from 'react-native';

// Cache for getCurrentUser with 5-minute TTL
let cachedUser: User | null = null;
let cacheTimestamp: number = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Cache for auth state listener
let lastUserId: string | null = null;
let cachedProfile: User | null = null;

export const authService = {
  async signUp(
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    preferredLanguage: 'en' | 'nl',
    clubIds?: string[]
  ) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          first_name: firstName,
          last_name: lastName,
          preferred_language: preferredLanguage,
        },
      },
    });
    
    if (error) throw error;
    
    // Associate user with clubs if provided
    if (data.user && clubIds && clubIds.length > 0) {
      try {
        await this.associateUserWithClubs(data.user.id, clubIds);
      } catch (clubError) {
        console.warn('Failed to associate user with clubs:', clubError);
        // Don't throw - club association failure shouldn't break registration
      }
    }

    // Track signup for analytics
    analyticsService.trackSignup();

    return data;
  },

  /**
   * Associate user with multiple clubs during registration
   * Internal method called by signUp
   */
  async associateUserWithClubs(userId: string, clubIds: string[]): Promise<void> {
    const clubMemberships = clubIds.map(clubId => ({
      user_id: userId,
      club_id: clubId,
      role: 'member' as const,
    }));

    const { error } = await supabase
      .from('user_clubs')
      .insert(clubMemberships);

    if (error) throw error;
  },

  async signIn(email: string, password: string) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;

    // Set Sentry user context for error attribution
    if (data.user) {
      setSentryUser(data.user.id);
    }

    return data;
  },

  async signOut() {
    cachedUser = null;
    cacheTimestamp = 0;

    // Clear Sentry user context
    clearSentryUser();

    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  },

  async updateProfile(updates: { first_name?: string; last_name?: string; avatar_url?: string }): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', user.id);

    if (error) throw error;

    // Invalidate all caches so next getCurrentUser/auth listener fetch fresh data
    cachedUser = null;
    cacheTimestamp = 0;
    cachedProfile = null;
    lastUserId = null;
  },

  async getCurrentUser(forceRefresh = false): Promise<User | null> {
    const now = Date.now();

    // Return cached user if valid
    if (!forceRefresh && cachedUser && (now - cacheTimestamp) < CACHE_TTL) {
      return cachedUser;
    }

    // Use getSession() (reads from local storage) instead of getUser()
    // (server roundtrip) to avoid hanging the bootstrap on flaky networks.
    // Anonymous users return instantly; subsequent profile/API calls still
    // validate the JWT server-side, so security is unchanged.
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;

    if (!user) {
      cachedUser = null;
      return null;
    }

    // Fetch user profile from profiles table for structured data.
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .maybeSingle();

    if (error) {
      console.error('Failed to fetch user profile:', error);
      // Fallback to auth user data if profile fetch fails
      return {
        id: user.id,
        email: user.email!,
        first_name: user.user_metadata?.first_name || '',
        last_name: user.user_metadata?.last_name || '',
        preferred_language: user.user_metadata?.preferred_language || 'nl',
        is_platform_admin: false,
        name: user.user_metadata?.name,
        avatar_url: user.user_metadata?.avatar_url,
        created_at: user.created_at!,
        onboarding_completed: false,
        onboarding_completed_at: null,
      };
    }

    if (!profile) {
      cachedUser = null;
      return null;
    }

    const userProfile: User = {
      id: profile.id,
      email: profile.email,
      first_name: profile.first_name,
      last_name: profile.last_name,
      preferred_language: profile.preferred_language,
      is_platform_admin: profile.is_platform_admin || false,
      name: profile.name,
      avatar_url: profile.avatar_url,
      created_at: profile.created_at,
      onboarding_completed: profile.onboarding_completed || false,
      onboarding_completed_at: profile.onboarding_completed_at || null,
    };

    cachedUser = userProfile;
    cacheTimestamp = now;

    return userProfile;
  },

  onAuthStateChange(callback: (user: User | null, event: string) => void) {
    return supabase.auth.onAuthStateChange(async (event, session) => {
      if (session?.user) {
        // Only re-fetch if user ID changed (optimization for token refresh)
        if (lastUserId !== session.user.id) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', session.user.id)
            .maybeSingle();

          if (profile) {
            cachedProfile = {
              id: profile.id,
              email: profile.email,
              first_name: profile.first_name,
              last_name: profile.last_name,
              preferred_language: profile.preferred_language,
              is_platform_admin: profile.is_platform_admin || false,
              name: profile.name,
              avatar_url: profile.avatar_url,
              created_at: profile.created_at,
              onboarding_completed: profile.onboarding_completed || false,
              onboarding_completed_at: profile.onboarding_completed_at || null,
            };
          } else {
            // Fallback to session user metadata if profile not found
            cachedProfile = {
              id: session.user.id,
              email: session.user.email!,
              first_name: session.user.user_metadata?.first_name || '',
              last_name: session.user.user_metadata?.last_name || '',
              preferred_language: session.user.user_metadata?.preferred_language || 'nl',
              is_platform_admin: false,
              name: session.user.user_metadata?.name,
              avatar_url: session.user.user_metadata?.avatar_url,
              onboarding_completed: false,
              onboarding_completed_at: null,
              created_at: session.user.created_at!,
            };
          }
          lastUserId = session.user.id;
        }

        callback(cachedProfile, event);
      } else {
        lastUserId = null;
        cachedProfile = null;
        callback(null, event);
      }
    });
  },

  async resetPassword(email: string) {
    const redirectTo = Platform.OS === 'web'
      ? `${window.location.origin}/reset-password`
      : 'clubswap://reset-password';
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo,
    });
    if (error) throw error;
  },

  async verifyCurrentPassword(email: string, password: string) {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
  },

  async updatePassword(newPassword: string) {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    if (error) throw error;
  },
};
